import { makeAutoObservable } from "mobx";

import { ApiConnector } from "@/api/api-connector";
import { apiRoute } from "@/config/config";

import {
  apiGetUsersMe,
  PermissionBit,
  PermissionMetadataResponse,
  UserRole,
} from "../../api";

const apiBase = `${apiRoute}/api/v1`;
const adminUsername = "admin";
const refreshTokenKey = "devtools:adminRefreshToken";

/**
 * Handles the dev-tool's permissions flow: looks up the current user's access
 * policy via their regular session, then mutates it as the `admin` user.
 *
 * Bypasses the regular ApiConnector for the admin-side calls so the logged-in
 * user's session is not affected, and holds an admin refresh token in
 * localStorage so the dev-tool only prompts for the password once per session.
 *
 * Public methods self-orchestrate: if no admin session is cached they surface
 * the password prompt via observables and resume once `submitPassword` succeeds.
 */
export class PermissionsClient {
  isWorking = false;
  needsPassword = false;
  passwordError: string | null = null;

  private passwordRequest: {
    resolve: () => void;
    reject: (e: Error) => void;
  } | null = null;

  constructor(private readonly apiConnector: ApiConnector) {
    makeAutoObservable(this);
  }

  get isAdmin(): boolean {
    return this.apiConnector.username === adminUsername;
  }

  /**
   * Apply `permissions` to the currently logged-in user's access policy and
   * reload the page so the new permissions take effect. Silent no-op if the
   * user dismisses the admin-password prompt.
   */
  async applyToCurrentUser(permissions: PermissionBit[]): Promise<void> {
    const { data } = await apiGetUsersMe({
      meta: { ignore401: true, ignore428: true },
    });
    const policyUuid = data?.access_policy?.uuid;
    if (!policyUuid) throw new Error("No access policy found");

    try {
      await this.runAsAdmin(async (token) => {
        const res = await fetch(`${apiBase}/policies/${policyUuid}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ permissions }),
          credentials: "omit",
        });
        if (!res.ok) throw new Error("Permission update failed");
      });
    } catch (e) {
      if (e instanceof PasswordPromptCancelled) return;
      throw e;
    }
    window.location.reload();
  }

  async loadPermissionsMetadata(): Promise<PermissionMetadataResponse> {
    return this.runAsAdmin(async (token) => {
      const res = await fetch(`${apiBase}/policies/permissions-metadata`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "omit",
      });
      if (!res.ok) throw new Error("Permission metadata fetch failed");
      return res.json() as Promise<PermissionMetadataResponse>;
    });
  }

  async submitPassword(password: string): Promise<void> {
    if (!this.passwordRequest) return;
    this.passwordError = null;
    this.isWorking = true;

    try {
      await this.getAccessToken(password);
      this.passwordRequest.resolve();
    } catch {
      this.forgetSession();
      this.passwordError = "Invalid admin password.";
      this.isWorking = false;
    }
  }

  cancelPasswordPrompt(): void {
    if (!this.passwordRequest) return;
    this.passwordRequest.reject(new PasswordPromptCancelled());
  }

  private async runAsAdmin<T>(
    action: (token: string) => Promise<T>,
  ): Promise<T> {
    this.isWorking = true;
    try {
      await this.ensureSession();
      const token = await this.getAccessToken();
      return await action(token);
    } finally {
      this.isWorking = false;
    }
  }

  private async ensureSession(): Promise<void> {
    try {
      await this.getAccessToken();
    } catch (e) {
      if (!(e instanceof NeedsPasswordError)) throw e;
      await this.waitForPassword();
    }
  }

  /**
   * Suspends until `submitPassword` resolves the deferred. While waiting,
   * `isWorking` flips back to `false` so the password dialog's Authenticate
   * button is interactive.
   */
  private async waitForPassword(): Promise<void> {
    this.isWorking = false;
    this.needsPassword = true;
    try {
      await new Promise<void>((resolve, reject) => {
        this.passwordRequest = { resolve, reject };
      });
    } finally {
      this.passwordRequest = null;
      this.needsPassword = false;
      this.passwordError = null;
      this.isWorking = true;
    }
  }

  private async getAccessToken(password?: string): Promise<string> {
    const storedToken = localStorage.getItem(refreshTokenKey);
    if (storedToken) {
      try {
        const result = await this.refresh(storedToken);
        this.storeRefreshToken(result.refresh_token);
        return result.access_token;
      } catch {
        this.forgetSession();
      }
    }

    if (!password) throw new NeedsPasswordError();

    const result = await this.login(password);
    this.storeRefreshToken(result.refresh_token);
    return result.access_token;
  }

  private async login(password: string): Promise<TokenPair> {
    const res = await fetch(`${apiBase}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: adminUsername,
        password,
      }),
      credentials: "omit",
    });
    if (!res.ok) throw new Error("Login failed");
    return res.json() as Promise<TokenPair>;
  }

  private async refresh(refreshToken: string): Promise<TokenPair> {
    const res = await fetch(`${apiBase}/auth/refresh`, {
      headers: { Authorization: `Bearer ${refreshToken}` },
      credentials: "omit",
    });
    if (!res.ok) throw new Error("Refresh failed");
    return res.json() as Promise<TokenPair>;
  }

  private forgetSession(): void {
    localStorage.removeItem(refreshTokenKey);
  }

  private storeRefreshToken(token: string): void {
    localStorage.setItem(refreshTokenKey, token);
  }
}

class NeedsPasswordError extends Error {}
class PasswordPromptCancelled extends Error {}

type TokenPair = { access_token: string; refresh_token: string };

const P = PermissionBit;

const ALL_PERMISSIONS = Object.values(PermissionBit).filter(
  (v): v is PermissionBit => typeof v === "number",
);

/**
 * Role → permission presets, mirroring `DEFAULT_ROLE_PERMISSIONS` in the backend.
 * Hand-maintained, so it can drift from the backend (see the in-sheet notice).
 *
 * @see {@link https://github.com/Seerlinq/db-api/blob/main/api/access_policy/permissions.py | db-api/api/access_policy/permissions.py}
 */
export const ROLE_PRESETS: Record<UserRole, PermissionBit[]> = {
  [UserRole["patient-ppg-app"]]: [P.VISIBILITY_SELF, P.GROUP_MEDICAL_ROUTERS],
  [UserRole["physician-ppg-app"]]: [
    P.VISIBILITY_MANAGED,
    P.GROUP_MEDICAL_ROUTERS,
  ],
  [UserRole["study-ppg-app"]]: [P.VISIBILITY_FULL, P.GROUP_MEDICAL_ROUTERS],
  [UserRole.patient]: [
    P.GROUP_MEDICAL_ROUTERS,
    P.UPDATE_OWNER,
    P.UPDATE_VISIBLE,
    P.READ,
    P.WRITE,
    P.VISIBILITY_SELF,
    P.PPG_META,
    P.ECG_META,
  ],
  [UserRole.physician]: [
    P.RUN_HEART_CORE_QUICKLOOK,
    P.PPG_RAW,
    P.ECG_RAW,
    P.GROUP_MEDICAL_ROUTERS,
    P.READ,
    P.WRITE,
    P.UPDATE_VISIBLE,
    P.DELETE_VISIBLE,
    P.VISIBILITY_MANAGED,
    P.VISIBILITY_PHYSICIAN,
  ],
  [UserRole["study-physician"]]: [
    P.RUN_HEART_CORE_QUICKLOOK,
    P.ACCESS_DRI,
    P.PPG_RAW,
    P.ECG_RAW,
    P.AFIB,
    P.GROUP_MONITORING_ROUTERS,
    P.GROUP_MEDICAL_ROUTERS,
    P.READ,
    P.WRITE,
    P.UPDATE_VISIBLE,
    P.DELETE_VISIBLE,
    P.VISIBILITY_MANAGED,
    P.VIEW_ANALYTICS,
  ],
  [UserRole["seerlinq-user"]]: [
    P.GROUP_MONITORING_ROUTERS,
    P.GROUP_MEDICAL_ROUTERS,
    P.READ,
    P.WRITE,
    P.UPDATE_VISIBLE,
    P.DELETE_VISIBLE,
    P.VISIBILITY_FULL,
    P.ACCESS_DRI,
    P.RUN_HEART_CORE_QUICKLOOK,
    P.ACCESS_LVFP,
    P.PPG_RAW,
    P.ECG_RAW,
    P.AFIB,
    P.VIEW_ANALYTICS,
    P.ACCESS_AI_CHAT,
  ],
  // VISIBILITY_SELF is forbidden alongside VISIBILITY_FULL on the update path,
  // even though the backend grants it at user creation for admins.
  [UserRole.admin]: ALL_PERMISSIONS.filter((p) => p !== P.VISIBILITY_SELF),
};
